import { TEnrichments } from '@/features/programs/types';
import { State } from '@/types';

type TChannelFormEnrichments = {
  stt: boolean;
  logos: boolean;
  peoples: boolean;
  ocr: boolean;
  advertising: boolean;
};

type TChannelFormParameters = {
  logo?: string;
  enrichments?: Partial<TChannelFormEnrichments>;
};

type TChannelForm = {
  state: string;
  uri: string;
  name: string;
  media_type: ChannelMediaTypeEnum;
  short_name: string;
  av_code: string | null;
  parent_channel_id: string;
  logo?: File | string | null;
  language_id?: string | null;
  parameters?: TChannelFormParameters | null;
  backup_channel_id?: string | null;
  audience_channel_source_id?: string | null;
  economical_value_channel_source_id?: string | null;
  region_code?: string | null;
  subregion_code?: string | null;
  country_code: string;
  scope: ChannelScopeEnum | null;
  provider?: ChannelProviderEnum | null;
  type?: ChannelTypeEnum;
};

export enum ChannelScopeEnum {
  REGIONAL = 'regional',
  LOCAL = 'local',
  NACIONAL = 'national',
  SUBREGIONAL = 'subregional',
}

export enum ChannelMediaTypeEnum {
  TV = 'tv',
  RADIO = 'r',
  AUDIO_PODCAST = 'AUDIO_PODCAST',
  STREAMING = 'STREAMING',
}

/**
 * Represents the parameters for a channel configuration.
 */
export type ChannelParametersType = {
  /**
   * URL of the channel's logo image.
   */
  logo?: string;

  /**
   * A pre-signed URL for read access to the channel's logo.
   */
  logo_presigned_url?: string;

  /**
   * Optional enrichment settings for the channel.
   */
  enrichments?: TEnrichments;
};

export enum ChannelProviderEnum {
  AUDITMEDIA = 'auditmedia',
  STACKSCALE = 'stackscale',
  STREAM = 'stream',
  ACCESO = 'acceso',
}

export enum ChannelTypeEnum {
  BACKUP = 'backup',
  TERRITORIAL_DISCONNECTION = 'territorial_disconnection',
  MAIN_BROADCAST = 'main_broadcast',
}

export type ChannelType = {
  id: string;
  state: State;
  uri: string;
  name: string;
  media_type: ChannelMediaTypeEnum;
  scope: ChannelScopeEnum;
  av_code: string | null;
  country_code: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string | null;
  short_name: string | null;
  region_code: string | null;
  subregion_code: string;
  parent_channel_id: string | null;
  parameters: ChannelParametersType | null;
  language_id: string | null;
  audience_channel_source_id: string | null;
  economical_value_channel_source_id: string | null;
  backup_channel_id: string | null;
  provider: ChannelProviderEnum;
  type?: ChannelTypeEnum;
};

export enum AudienceProviderEnum {
  EGM = 'egm',
  KANTAR = 'kantar',
  IBOPE = 'ibope',
}

export enum EconomicalValueProviderEnum {
  MECOS = 'mecos',
}

export type ChannelSourceType<Provider> = {
  id: string;
  state: string;
  name: string;
  media_type: ChannelMediaTypeEnum;
  provider: Provider;
  scope: string;
  country_code: string;
  created_at: string;
  region_code: string | null;
  subregion_code: string | null;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
};

export type AudienceChannelSourceType = ChannelSourceType<AudienceProviderEnum>;
export type EconomicalValueChannelSourceType = ChannelSourceType<EconomicalValueProviderEnum>;

export type ChannelFilters = {
  idIn: string[];
  state: string;
  mediaTypeIn: ChannelMediaTypeEnum[];
  isParentChannel: boolean;
  parentChannelId: string;
  scope: ChannelScopeEnum;
  countryCode: string;
  regionCode: string;
  subregionCode: string;
  updatedByIn: string[];
  programBroadcastStartDatetimeGte?: string;
  programBroadcastStartDatetimeLte?: string;
  segmentStartDatetimeGte?: string;
  segmentEndDatetimeLte?: string;
  clipStartDatetimeGte?: string;
  clipEndDatetimeLte?: string;
};

export type AudienceChannelSourceFilters = {
  idIn?: string[];
  includeIds?: string[];
  scopeIn?: ChannelScopeEnum;
  mediaTypeIn?: ChannelMediaTypeEnum;
  providerIn?: AudienceProviderEnum[];
  countryIn?: string;
  regionIn?: string;
  name?: string;
  limit?: number;
};

export type EconomicalValueChannelSourceFilters = {
  idIn?: string[];
  scopeIn?: ChannelScopeEnum;
  mediaTypeIn?: ChannelMediaTypeEnum;
  providerIn?: EconomicalValueProviderEnum;
  countryIn?: string;
  regionIn?: string;
  limit?: number;
};

export type { TChannelForm };
